import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ApiURl from '../Paths/Api';

const MeetingSearch = () => {
  const [searchType, setSearchType] = useState('phone');
  const [searchValue, setSearchValue] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [personSearchValue, setPersonSearchValue] = useState('');
  const [personDetails, setPersonDetails] = useState(null);
  const [personLoading, setPersonLoading] = useState(false);
  const [personError, setPersonError] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const navigate = useNavigate();

  // Search Meetings
  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchValue.trim()) {
      setError('Please enter a search value');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      let response;
      if (searchType === 'phone') {
        response = await fetch(`${ApiURl}/get_meetings.php`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ phone: searchValue }),
        });
      } else {
        response = await fetch(`${ApiURl}/search_meetings.php`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: searchValue }),
        });
      }
      if (!response.ok) throw new Error(`HTTP error: ${response.status}`);
      const data = await response.json();
      if (data.success) {
        setSearchResults(data.data || []);
      } else {
        setError(data.message || 'No results found');
        setSearchResults([]);
      }
    } catch (err) {
      setError(err.message || 'An error occurred while searching');
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  // Search Person inside Meeting
  const handlePersonSearch = async () => {
    if (!personSearchValue.trim() || !selectedMeeting) return;
    setPersonLoading(true);
    setPersonError(null);
    try {
      const response = await fetch(`${ApiURl}/search_person.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          meetingId: selectedMeeting.id,
          query: personSearchValue,
        }),
      });
      if (!response.ok) throw new Error(`HTTP error: ${response.status}`);
      const data = await response.json();
      if (data.success) {
        setPersonDetails(data.data);
      } else {
        setPersonError(data.message || 'No person found');
        setPersonDetails(null);
      }
    } catch (err) {
      setPersonError(err.message || 'Error fetching person details');
      setPersonDetails(null);
    } finally {
      setPersonLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 font-sans flex flex-col">
      {/* Navbar */}
      <nav className="bg-gradient-to-b from-teal-600 to-teal-800 text-white p-4 shadow-lg fixed w-full top-0 z-30">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <h1 className="text-lg font-bold tracking-tight">Meeting Search</h1>
          <button
            className="md:hidden p-2 rounded-full hover:bg-teal-600 transition-all duration-300"
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>
      </nav>

      <div className="flex flex-1 mt-14">
        {/* Sidebar */}
        <div
          className={`fixed top-14 left-0 h-[calc(100vh-3.5rem)] bg-gradient-to-b from-teal-600 to-teal-800 text-white w-16 flex flex-col items-center py-4 space-y-4 transition-transform duration-300 ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0 md:w-16 z-20`}
        >
          <button
            className="p-2 rounded-full bg-teal-700 transition-all duration-300 transform hover:scale-110 flex items-center w-full justify-center group"
            title="Dashboard"
            onClick={() => navigate('/dashboard')}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
            <span className="absolute left-16 bg-teal-700 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">Dashboard</span>
          </button>
          <button
            className="p-2 rounded-full hover:bg-teal-600 transition-all duration-300 transform hover:scale-110 flex items-center w-full justify-center group"
            title="Create Meeting"
            onClick={() => navigate('/create-meeting')}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
            </svg>
            <span className="absolute left-16 bg-teal-700 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">Create Meeting</span>
          </button>
          <button
            className="p-2 rounded-full hover:bg-teal-600 transition-all duration-300 transform hover:scale-110 flex items-center w-full justify-center group"
            title="View Meetings"
            onClick={() => navigate('/view-meetings')}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <span className="absolute left-16 bg-teal-700 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">Reports</span>
          </button>
          <button
            className="p-2 rounded-full hover:bg-teal-600 transition-all duration-300 transform hover:scale-110 flex items-center w-full justify-center group"
            title="Send Invites"
            onClick={() => navigate('/send-invites')}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7-4 7 4v8l-7 4-7-4V8zm0 0l7 4 7-4m-14 0v8l7 4 7-4V8" />
            </svg>
            <span className="absolute left-16 bg-teal-700 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">Invite</span>
          </button>
          <button
            className="p-2 rounded-full hover:bg-teal-600 transition-all duration-300 transform hover:scale-110 flex items-center w-full justify-center group"
            title="Search Meetings"
            onClick={() => navigate('/search-form')}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <span className="absolute left-16 bg-teal-700 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">Search</span>
          </button>
          <button
            className="p-2 rounded-full hover:bg-teal-600 transition-all duration-300 transform hover:scale-110 flex items-center w-full justify-center group"
            title="Send Invite Without Pay"
            onClick={() => navigate('/adminsend')}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7-4 7 4v8l-7 4-7-4V8zm0 0l7 4 7-4m-14 0v8l7 4 7-4V8" />
            </svg>
            <span className="absolute left-16 bg-teal-700 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">Send Invite</span>
          </button>
          <button
            className="p-2 rounded-full hover:bg-teal-600 transition-all duration-300 transform hover:scale-110 flex items-center w-full justify-center group"
            title="Restrict Meeting"
            onClick={() => navigate('/restrict-meeting')}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c0 1.104-.896 2-2 2s-2-.896-2-2 2-4 2-4 2 2.896 2 4zm0 0c0 1.104.896 2 2 2s2-.896 2-2-2-4-2-4-2 2.896-2 4zm-6 7c0 1.104.896 2 2 2s2-.896 2-2-2-4-2-4-2 2.896-2 4zm12 0c0 1.104.896 2 2 2s2-.896 2-2-2-4-2-4-2 2.896-2 4z" />
            </svg>
            <span className="absolute left-16 bg-teal-700 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">Restrict Meeting</span>
          </button>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-4 ml-0 md:ml-16 max-w-7xl mx-auto w-full">
          {/* Main Search Area */}
          <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Search Meetings</h2>
            <form onSubmit={handleSearch} className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex items-center space-x-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="searchType"
                      value="phone"
                      checked={searchType === 'phone'}
                      onChange={(e) => setSearchType(e.target.value)}
                      className="mr-2"
                    />
                    Phone Number
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="searchType"
                      value="name"
                      checked={searchType === 'name'}
                      onChange={(e) => setSearchType(e.target.value)}
                      className="mr-2"
                    />
                    Name
                  </label>
                </div>

                <div className="flex-1">
                  <input
                    type={searchType === 'phone' ? 'tel' : 'text'}
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    placeholder={searchType === 'phone' ? 'Enter phone number...' : 'Enter name...'}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-teal-500 text-white rounded-md hover:bg-teal-600 transition-colors duration-200 disabled:opacity-50"
                >
                  {loading ? 'Searching...' : 'Search'}
                </button>
              </div>
            </form>

            {error && (
              <div className="mt-4 p-3 bg-red-100 text-red-600 rounded-md">
                {error}
              </div>
            )}
          </div>

          {/* Results */}
          {searchResults.length > 0 && (
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Select a Meeting</h3>
              <div className="space-y-4">
                {searchResults.map((meeting) => (
                  <div
                    key={meeting.id}
                    onClick={() => setSelectedMeeting(meeting)}
                    className={`border rounded-lg p-4 cursor-pointer transition duration-200 ${selectedMeeting?.id === meeting.id ? 'border-teal-500 bg-teal-50' : 'border-gray-200 hover:shadow-md'
                      }`}
                  >
                    <h4 className="font-semibold text-gray-800">{meeting.purpose || 'No purpose'}</h4>
                    <p className="text-sm text-gray-600">{new Date(meeting.time).toLocaleString()}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right Sidebar */}
        {selectedMeeting && (
          <aside className="w-80 bg-white border-l border-gray-200 p-4 shadow-lg">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Meeting Details</h3>
            <p><span className="font-medium">Purpose:</span> {selectedMeeting.purpose}</p>
            <p><span className="font-medium">Time:</span> {new Date(selectedMeeting.time).toLocaleString()}</p>
            <p><span className="font-medium">Location:</span> {selectedMeeting.location}</p>

            {/* Person Search */}
            <div className="mt-4">
              <h4 className="text-md font-semibold mb-2">Search Person</h4>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={personSearchValue}
                  onChange={(e) => setPersonSearchValue(e.target.value)}
                  placeholder="Enter name or phone"
                  className="flex-1 px-2 py-1 border border-gray-300 rounded-md"
                />
                <button
                  onClick={handlePersonSearch}
                  disabled={personLoading}
                  className="px-3 py-1 bg-teal-500 text-white rounded-md disabled:opacity-50"
                >
                  {personLoading ? '...' : 'Go'}
                </button>
              </div>
              {personError && <p className="text-red-500 text-sm mt-2">{personError}</p>}
              {personDetails && (
                <div className="mt-3 p-2 border rounded-md bg-gray-50">
                  <p><span className="font-medium">Name:</span> {personDetails.name}</p>
                  <p><span className="font-medium">Phone:</span> {personDetails.phone}</p>
                  <p><span className="font-medium">Status:</span> {personDetails.attended ? 'Present' : 'Absent'}</p>
                </div>
              )}
            </div>
          </aside>
        )}
      </div>
    </div>
  );
};

export default MeetingSearch;